import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaPlus, FaTrash, FaLayerGroup, FaTimes, FaEdit, FaTrophy, FaUsers, FaCheck } from 'react-icons/fa';
import './RankManagement.css';

const RankManagement = () => {
  const [ranks, setRanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('ranks');
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Modal states
  const [showAddRankModal, setShowAddRankModal] = useState(false);
  const [showAddLevelModal, setShowAddLevelModal] = useState(false);
  const [showDeleteLevelModal, setShowDeleteLevelModal] = useState(false);

  // Form states
  const [newRank, setNewRank] = useState({
    rank_number: '',
    min_active_clients: '',
        min_deposit: '',
    rank_name: ''

  });
  const [newLevel, setNewLevel] = useState({
    level: '',
    types: ['standard', 'cent', 'pro', 'prime', 'pamm', 'demo']
  });
  const [deleteLevel, setDeleteLevel] = useState({
    level: '',
    type: ''
  });

  // Get all account types dynamically from the data
  const getAccountTypes = () => {
    if (ranks.length === 0) return ['standard', 'cent', 'pro', 'prime', 'pamm', 'demo'];
    const firstRank = ranks[0];
    const types = new Set();
    Object.keys(firstRank).forEach(key => {
      const match = key.match(/level_\d+_(\w+)_commission/);
      if (match) types.add(match[1]);
    });
    // Return sorted types, with standard types first
    const standardOrder = ['standard', 'cent', 'pro', 'prime', 'pamm', 'demo'];
    const sortedTypes = Array.from(types).sort((a, b) => {
      const indexA = standardOrder.indexOf(a);
      const indexB = standardOrder.indexOf(b);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
    return sortedTypes.length > 0 ? sortedTypes : ['standard', 'cent', 'pro', 'prime', 'pamm', 'demo'];
  };

  // Get all levels from the data
  const getLevels = () => {
    if (ranks.length === 0) return [];
    const firstRank = ranks[0];
    const levels = new Set();
    Object.keys(firstRank).forEach(key => {
      const match = key.match(/level_(\d+)_/);
      if (match) levels.add(parseInt(match[1]));
    });
    return Array.from(levels).sort((a, b) => a - b);
  };

  // Dynamic account types
  const accountTypes = getAccountTypes();

  // Fetch ranks data
  const fetchRanks = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://server.account.majestyfx.com/admin_fetch_ranks.php');
      if (response.data.success) {
        setRanks(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to fetch ranks');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };
console.log(ranks);

  useEffect(() => {
    fetchRanks();
  }, []);

  // Handle cell edit
  const handleCellClick = (rankId, field, currentValue) => {
    setEditingCell({ rankId, field });
    setEditValue(currentValue);
  };

  // Save cell edit
  const handleCellSave = async (rankId, field) => {
    setSaving(true);
    try {
      const payload = {
        rank_id: rankId,
        [field]: parseFloat(editValue) || editValue
      };

      const response = await axios.post(
        'https://server.account.majestyfx.com/admin_update_ranks.php',
        payload
      );

      if (response.data.success) {
        setEditingCell(null);
        // Refresh data from API
        fetchRanks();
      } else {
        alert(response.data.message || 'Update failed');
      }
    } catch (err) {
      alert('Network error. Please try again.');
      console.error('Update error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Handle key press in edit mode
  const handleKeyPress = (e, rankId, field) => {
    if (e.key === 'Enter') {
      handleCellSave(rankId, field);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  // Add new rank
  const handleAddRank = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.post(
        'https://server.account.majestyfx.com/admin_add_ranks.php',
        {
          rank_number: parseInt(newRank.rank_number),
          min_active_clients: parseInt(newRank.min_active_clients),
          min_deposit: parseFloat(newRank.min_deposit),
          rank_name: newRank.rank_name
        }
      );

      if (response.data.success) {
        alert('Rank added successfully!');
        setShowAddRankModal(false);
        setNewRank({ rank_number: '', min_active_clients: '', min_deposit: '', rank_name: '' });
        fetchRanks();
      } else {
        alert(response.data.message || 'Failed to add rank');
      }
    } catch (err) {
      alert('Network error. Please try again.');
      console.error('Add rank error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Delete rank
  const handleDeleteRank = async (rankId, rankName) => {
    if (!window.confirm(`Are you sure you want to delete "${rankName}"?`)) return;

    try {
      const response = await axios.post(
        'https://server.account.majestyfx.com/admin_delete_ranks.php',
        { rank_id: parseInt(rankId) }
      );

      if (response.data.success) {
        alert('Rank deleted successfully!');
        fetchRanks();
      } else {
        alert(response.data.message || 'Failed to delete rank');
      }
    } catch (err) {
      alert('Network error. Please try again.');
      console.error('Delete rank error:', err);
    }
  };

  // Add more levels
  const handleAddLevel = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.post(
        'https://server.account.majestyfx.com/admin_add_morelevelsranks.php',
        {
          level: parseInt(newLevel.level),
          types: newLevel.types
        }
      );

      if (response.data.success) {
        alert('Level added successfully!');
        setShowAddLevelModal(false);
        setNewLevel({ level: '', types: ['standard', 'cent', 'pro', 'prime', 'pamm', 'demo'] });
        fetchRanks();
      } else {
        alert(response.data.message || 'Failed to add level');
      }
    } catch (err) {
      alert('Network error. Please try again.');
      console.error('Add level error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Delete level
  const handleDeleteLevel = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Are you sure you want to delete Level ${deleteLevel.level} - ${deleteLevel.type}?`)) return;

    setSaving(true);
    try {
      const response = await axios.post(
        'https://server.account.majestyfx.com/admin_delete_rankslevels.php',
        {
          level: parseInt(deleteLevel.level),
          type: deleteLevel.type
        }
      );

      if (response.data.success) {
        alert('Level deleted successfully!');
        setShowDeleteLevelModal(false);
        setDeleteLevel({ level: '', type: '' });
        fetchRanks();
      } else {
        alert(response.data.message || 'Failed to delete level');
      }
    } catch (err) {
      alert('Network error. Please try again.');
      console.error('Delete level error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Toggle type selection for adding levels
  const toggleTypeSelection = (type) => {
    setNewLevel(prev => ({
      ...prev,
      types: prev.types.includes(type)
        ? prev.types.filter(t => t !== type)
        : [...prev.types, type]
    }));
  };

  const levels = getLevels();

  // Render editable cell
  const renderEditableCell = (rank, field, value) => {
    const isEditing = editingCell?.rankId === rank.rank_id && editingCell?.field === field;

    if (isEditing) {
      return (
        <input
          type="text"
          className="edit-input"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => handleCellSave(rank.rank_id, field)}
          onKeyDown={(e) => handleKeyPress(e, rank.rank_id, field)}
          autoFocus
          disabled={saving}
        />
      );
    }

  return (
  <span
    className="editable-wrapper"
    title="Click to edit"
    onClick={() => handleCellClick(rank.rank_id, field, value)}
  >
    <span className="editable-value">
      {value}
    </span>
    <FaEdit className="edit-icon" />
  </span>
);

  };

  return (
    <div className="rank-management-container">
      <div className="page-header">
        <div className="header-content">
          <h1><FaTrophy style={{ marginRight: '10px' }} />Rank Management</h1>
          <p>Manage IB ranks and commission levels</p>
        </div>
        <div className="header-actions">
          <button className="action-btn add" onClick={() => setShowAddRankModal(true)}>
            <FaPlus /> Add Rank
          </button>
          <button className="action-btn level" onClick={() => setShowAddLevelModal(true)}>
            <FaLayerGroup /> Add Level
          </button>
          <button className="action-btn delete" onClick={() => setShowDeleteLevelModal(true)}>
            <FaTrash /> Delete Level
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'ranks' ? 'active' : ''}`}
          onClick={() => setActiveTab('ranks')}
        >
          <FaUsers style={{ marginRight: '6px' }} />Rank Overview
        </button>
        {levels.map(level => (
          <button
            key={level}
            className={`tab ${activeTab === `level-${level}` ? 'active' : ''}`}
            onClick={() => setActiveTab(`level-${level}`)}
          >
            Level {level}
          </button>
        ))}
      </div>

      {/* Error Message */}
      {error && <div className="error-message">{error}</div>}

      {/* Loading State */}
      {loading ? (
        <div className="loading">Loading ranks data...</div>
      ) : (
        <>
          {/* Rank Overview Tab */}
          {activeTab === 'ranks' && (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Rank ID</th>
                    <th>Rank Number</th>
                    <th>Rank Name</th>
                    <th>Minimum Deposit</th>

                    <th>Min Active Clients</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ranks.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="no-data">No ranks found</td>
                    </tr>
                  ) : (
                    ranks.map(rank => (
                      <tr key={rank.rank_id}>
                        <td>{rank.rank_id}</td>
                        <td>{renderEditableCell(rank, 'rank_number', rank.rank_number)}</td>
                        <td>{renderEditableCell(rank, 'rank_name', rank.rank_name)}</td>
                        <td>
  {renderEditableCell(rank, 'min_deposit', rank.min_deposit)}
</td>

                        <td>{renderEditableCell(rank, 'min_active_clients', rank.min_active_clients)}</td>
                        <td>
                          <button 
                            className="delete-btn"
                            onClick={() => handleDeleteRank(rank.rank_id, rank.rank_name)}
                            title="Delete rank"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Level Tabs */}
          {levels.map(level => (
            activeTab === `level-${level}` && (
              <div key={level} className="table-container">
                <div className="level-header">
                  <h2>Level {level} Commissions</h2>
                  <p className="hint"><FaEdit style={{ marginRight: '4px' }} />Click on any commission value to edit</p>
                </div>
                <table className="data-table commission-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      {accountTypes.map(type => (
                        <th key={type} className="type-header">
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ranks.map(rank => (
                      <tr key={rank.rank_id}>
                        <td className="rank-name-cell">
                          <span className="rank-badge">{rank.rank_name}</span>
                        </td>
                        {accountTypes.map(type => {
                          const field = `level_${level}_${type}_commission`;
                          const value = rank[field];
                          return (
                            <td key={type} className="commission-cell">
                              {value !== undefined ? (
                                renderEditableCell(rank, field, value)
                              ) : (
                                <span className="na">N/A</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ))}
        </>
      )}

      {/* Add Rank Modal */}
      {showAddRankModal && (
        <div className="modal-overlay" onClick={() => setShowAddRankModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FaPlus style={{ marginRight: '8px' }} />Add New Rank</h2>
              <button className="close-btn" onClick={() => setShowAddRankModal(false)}><FaTimes /></button>
            </div>
            <form onSubmit={handleAddRank}>
              <div className="form-group">
                <label>Rank Number *</label>
                <input
                  type="number"
                  value={newRank.rank_number}
                  onChange={(e) => setNewRank(prev => ({ ...prev, rank_number: e.target.value }))}
                  placeholder="e.g., 5"
                  required
                  min="1"
                />
              </div>
              <div className="form-group">
                <label>Rank Name *</label>
                <input
                  type="text"
                  value={newRank.rank_name}
                  onChange={(e) => setNewRank(prev => ({ ...prev, rank_name: e.target.value }))}
                  placeholder="e.g., Diamond"
                  required
                />
              </div>
              <div className="form-group">
  <label>Minimum Deposit *</label>
  <input
    type="number"
    value={newRank.min_deposit}
    onChange={(e) =>
      setNewRank(prev => ({ ...prev, min_deposit: e.target.value }))
    }
    placeholder="e.g., 1000"
    required
    min="0"
  />
</div>

              <div className="form-group">
                <label>Minimum Active Clients *</label>
                <input
                  type="number"
                  value={newRank.min_active_clients}
                  onChange={(e) => setNewRank(prev => ({ ...prev, min_active_clients: e.target.value }))}
                  placeholder="e.g., 50"
                  required
                  min="0"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowAddRankModal(false)}>
                  <FaTimes /> Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={saving}>
                  <FaCheck /> {saving ? 'Adding...' : 'Add Rank'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Level Modal */}
      {showAddLevelModal && (
        <div className="modal-overlay" onClick={() => setShowAddLevelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FaLayerGroup style={{ marginRight: '8px' }} />Add Commission Level</h2>
              <button className="close-btn" onClick={() => setShowAddLevelModal(false)}><FaTimes /></button>
            </div>
            <form onSubmit={handleAddLevel}>
              <div className="form-group">
                <label>Level Number *</label>
                <input
                  type="number"
                  value={newLevel.level}
                  onChange={(e) => setNewLevel(prev => ({ ...prev, level: e.target.value }))}
                  placeholder="e.g., 9"
                  required
                  min="1"
                />
              </div>
              <div className="form-group">
                <label>Account Types *</label>
                <div className="type-checkboxes">
                  {['standard', 'cent', 'pro', 'prime', 'pamm', 'demo'].map(type => (
                    <label key={type} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newLevel.types.includes(type)}
                        onChange={() => toggleTypeSelection(type)}
                      />
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </label>
                  ))}
                </div>
                <div className="custom-type">
                  <input
                    type="text"
                    placeholder="Add custom type (optional)"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const customType = e.target.value.trim().toLowerCase();
                        if (customType && !newLevel.types.includes(customType)) {
                          setNewLevel(prev => ({ ...prev, types: [...prev.types, customType] }));
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <small>Press Enter to add custom type</small>
                </div>
                {newLevel.types.length > 0 && (
                  <div className="selected-types">
                    <strong>Selected:</strong> {newLevel.types.join(', ')}
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowAddLevelModal(false)}>
                  <FaTimes /> Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={saving || newLevel.types.length === 0}>
                  <FaCheck /> {saving ? 'Adding...' : 'Add Level'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Level Modal */}
      {showDeleteLevelModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteLevelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FaTrash style={{ marginRight: '8px' }} />Delete Commission Level</h2>
              <button className="close-btn" onClick={() => setShowDeleteLevelModal(false)}><FaTimes /></button>
            </div>
            <form onSubmit={handleDeleteLevel}>
              <div className="form-group">
                <label>Level Number *</label>
                <select
                  value={deleteLevel.level}
                  onChange={(e) => setDeleteLevel(prev => ({ ...prev, level: e.target.value }))}
                  required
                >
                  <option value="">Select Level</option>
                  {levels.map(level => (
                    <option key={level} value={level}>Level {level}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Account Type *</label>
                <select
                  value={deleteLevel.type}
                  onChange={(e) => setDeleteLevel(prev => ({ ...prev, type: e.target.value }))}
                  required
                >
                  <option value="">Select Type</option>
                  {accountTypes.map(type => (
                    <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="warning-box">
                ⚠️ Warning: This will delete the commission column for all ranks. This action cannot be undone.
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowDeleteLevelModal(false)}>
                  <FaTimes /> Cancel
                </button>
                <button type="submit" className="submit-btn danger" disabled={saving}>
                  <FaTrash /> {saving ? 'Deleting...' : 'Delete Level'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RankManagement;
